import { isEmpty } from 'lodash';
import React, { CSSProperties, Component, useCallback, useMemo, useState } from 'react';
import { MBView } from '@shared/components/MBView';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { SharedStyles, grid } from '@shared/styles';
import { MBAnalytics } from '@shared/util/analytics';
import { useMountedRef } from '@shared/util/hooks';
import { useBackgroundColor, useBorderColor, useForegroundColor } from './mbButtonThemeColorHooks';
import { MBButtonProps } from './mbButtonTypes';
import { Styles } from './styles';

export function MBButton({
  title,
  wrap,
  leftIcon,
  leftIconSize = grid(2),
  leftIconColor,
  leftIconStyle = { paddingRight: 5 },
  rightIcon,
  rightIconSize = grid(2),
  rightIconColor,
  rightIconStyle = { paddingRight: 5 },
  onPress,
  bold = true,
  size = 'normal',
  theme = 'normal',
  paddingHorizontal = 2.5,
  attributionId,
  testID,
  titleColor: titleColorOverride,
  borderColor: borderColorOverride,
  backgroundColor: backgroundColorOverride,
  loading: loadingProp,
  disabled: disabledProp,
  style,
}: MBButtonProps) {
  const mounted = useMountedRef();
  const [_loading, setLoading] = useState(false);
  const loading = loadingProp || _loading;
  const disabled = disabledProp || loading;

  const _backgroundColor = useBackgroundColor(theme, disabled);
  const backgroundColor = backgroundColorOverride || _backgroundColor;
  const foregroundColor = useForegroundColor(theme, disabled);
  const titleColor = titleColorOverride || foregroundColor;

  const _borderColor = useBorderColor(theme, disabled);
  const borderColor = borderColorOverride || _borderColor;

  const handlePress = useCallback(
    async (node: Component) => {
      if (!onPress) {
        return;
      }

      if (!!attributionId) {
        MBAnalytics.track('ButtonPress', { attributionId });
      }

      // Start loading if onPress returns promise
      const maybePromise = onPress(node);
      if (maybePromise instanceof Promise) {
        try {
          setLoading(true);
          await maybePromise;
        } finally {
          if (mounted.current) {
            setLoading(false);
          }
        }
      }
    },
    [attributionId, onPress, mounted],
  );

  const textSize = size === 'small' ? 'body2' : 'body1';

  const titleContent = useMemo(() => {
    if (React.isValidElement(title)) {
      return title;
    }

    return (
      <Text
        size={textSize}
        weight={bold ? 'semibold' : undefined}
        color={titleColor}
        style={wrap ? wrapStyle : undefined}
      >
        {title}
      </Text>
    );
  }, [bold, textSize, titleColor, title, wrap]);

  const content = (
    <MBView
      center
      paddingHorizontal={paddingHorizontal}
      style={[
        Styles.button,
        size === 'normal' && Styles.buttonSizeNormal,
        size === 'small' && Styles.buttonSizeSmall,
        { backgroundColor, borderColor },
        style,
      ]}
      testID={testID}
    >
      {/* If we're loading, overlay a loading indicator */}
      {loading && (
        <MBView center style={SharedStyles.absoluteCover}>
          <MBLoadingIndicator color={titleColor} />
        </MBView>
      )}

      {/*
        When loading, we make the text content invisible.
        Note: we still want to render it since that makes
        the button keep its shape. Otherwise it might be
        smaller while loading since the loading indicator
        probably is less wide than the text.
      */}
      <View row centerVertically style={{ opacity: loading ? 0 : 1 }}>
        {leftIcon && (
          <MBView style={leftIconStyle}>
            <MBIconV2
              name={leftIcon}
              color={!isEmpty(leftIconColor) ? leftIconColor : titleColor}
              size={leftIconSize}
            />
          </MBView>
        )}
        {titleContent}
        {rightIcon && (
          <MBView style={rightIconStyle}>
            <MBIconV2
              name={rightIcon}
              color={!isEmpty(rightIconColor) ? rightIconColor : titleColor}
              size={rightIconSize}
            />
          </MBView>
        )}
      </View>
    </MBView>
  );

  if (!onPress) {
    return <View>{content}</View>;
  }

  return (
    <MBTouchableOpacity onPress={handlePress} disabled={disabled}>
      {content}
    </MBTouchableOpacity>
  );
}

const wrapStyle: CSSProperties = {
  alignSelf: 'flex-start',
  whiteSpace: 'normal',
  flexGrow: 0,
  flexShrink: 1,
};
